/**
 * A route branch only staff and admins may enter.
 *
 * Menu visibility is not access control: a page kept out of the drawer is
 * still one typed URL away, and /support carries every tenant's conversations.
 * The API scopes what it returns regardless — this is about the page itself
 * being ours.
 *
 * A reporter sent here is almost always following the link in their own
 * notification mail, so bouncing them to the dashboard and saying nothing
 * makes their own conversation look broken. The ticket they asked for opens
 * in the bubble's panel instead, and only then do they go home.
 *
 * Home is /todo, not "/". "/" is the landing page, which sits OUTSIDE the App
 * layout and redirects here anyway — and that hop unmounts App, taking the
 * panel's provider and the conversation just opened in it down with it.
 */
import { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useGlobalState } from "./globalState";
import { PageLoader } from "./page-loader";
import { useSupportReport } from "./support/use-support-report";

/** The ticket id in `/support/17`, or null anywhere else under /support. */
const requestedTicketId = (pathname: string): number | null => {
	const match = /^\/support\/(\d+)/.exec(pathname);
	return match ? Number(match[1]) : null;
};

const StaffOnlyRoute: React.FC = () => {
	const { currentUser } = useGlobalState();
	const location = useLocation();
	const { openConversation } = useSupportReport();
	const [hasOpenedPanel, setHasOpenedPanel] = useState(false);

	const isStaff = Boolean(currentUser?.is_staff || currentUser?.is_superuser);
	const wanted = requestedTicketId(location.pathname);

	// Rendering <Navigate> straight away would unmount this before the effect
	// ever ran — a child's effects fire first, and Navigate's is the
	// navigation. So the redirect waits one commit for the panel to be told.
	useEffect(() => {
		if (!currentUser || isStaff || hasOpenedPanel) return;
		if (wanted !== null) openConversation(wanted);
		setHasOpenedPanel(true);
	}, [currentUser, isStaff, wanted, hasOpenedPanel, openConversation]);

	// Nobody is anybody until the app has fetched who they are; redirecting on
	// the way there would throw a staff member off their own page.
	if (!currentUser) return <PageLoader />;
	if (isStaff) return <Outlet />;
	if (!hasOpenedPanel) return <PageLoader />;
	return <Navigate to="/todo" replace />;
};

export default StaffOnlyRoute;
